import { useEffect, useState, useContext, ReactElement } from "react";
import { ThemeOptions, ThemeVariant } from "@bronte/viewer-api/src/main";
import { FONT_OPTIONS } from "../../shared/constants";
import { ThemeContext, IThemeContext } from "../../shared/ThemeContext";
import { getThemeVariantId } from "../../shared/themeVariants";
import {
  EtextWidgetTestConfig,
  EtextWidgetType,
  formatPayloadJson,
  getDefaultPayloadForType,
  parseEtextWidgetPayload,
} from "../../shared/etextWidgetTestConfig";
import "./styles.scss";
import { IViewerApi } from "@bronte/viewer-api/src/IViewerApi";

interface ISettingsDrawer {
  viewerApi: IViewerApi;
  minimalShell: boolean;
  onClose: () => void;
  parentActivityId?: string;
  showTestWidgetButton?: boolean;
  onLoadTestWidget?: (config: EtextWidgetTestConfig) => void;
}

interface IThemeVariant extends ThemeVariant {
  cssClass: string;
  label: string;
}

export const SettingsDrawer = ({
  viewerApi,
  minimalShell,
  onClose,
  parentActivityId,
  showTestWidgetButton = false,
  onLoadTestWidget,
}: ISettingsDrawer): ReactElement => {
  const { currentTheme, setCurrentTheme } = useContext(ThemeContext) as IThemeContext;
  const [themeVariants, setThemeVariants] = useState<IThemeVariant[]>([]);
  const [isAppInBackground, setIsAppInBackground] = useState(false);
  const [fontSize, setFontSize] = useState(currentTheme.fontSize);
  const [font, setFont] = useState(currentTheme.fontSelector);
  const [showHighlights, setShowHighlights] = useState(currentTheme.enableShowHide);
  const [highlightOptions, setHighlightOptions] = useState(
    JSON.stringify(currentTheme.highlight, null, 2),
  );
  const [widgetType, setWidgetType] = useState<EtextWidgetType>("mcq");
  const [widgetPayloadJson, setWidgetPayloadJson] = useState(() =>
    formatPayloadJson(getDefaultPayloadForType("mcq")),
  );

  const onUpdateTheme = (themeOptions?: Partial<ThemeOptions>): void => {
    const theme = { ...currentTheme, ...themeOptions };
    setCurrentTheme(theme);

    viewerApi.updateTheme(theme);
  };

  const onUpdateHighlightOptions = (highlightOptions: any): void => {
    setHighlightOptions(highlightOptions);
    try {
      const parsedValue = JSON.parse(highlightOptions);
      onUpdateTheme({ highlight: parsedValue });
    } catch (error) {
      console.error("Invalid JSON input", error);
    }
  };

  const handleVisibility = (): void => {
    const updatedShowHighlights = !showHighlights;

    setShowHighlights(updatedShowHighlights);
    onUpdateTheme({ enableShowHide: updatedShowHighlights });

    viewerApi.annotations().show(updatedShowHighlights);
  };

  useEffect(() => {
    const getThemeVariants = async (): Promise<void> => {
      let variants = await viewerApi.getThemeVariants();

      variants = variants?.map((variant: ThemeVariant) => ({
        ...variant,
        ...getVariantOptions(variant.variantName),
      }));

      setThemeVariants(variants as IThemeVariant[]);
    };

    getThemeVariants();
  }, []);

  const getVariantOptions = (variantName: string): { cssClass: string; label: string } => {
    switch (getThemeVariantId(variantName)) {
      case "sepia":
        return {
          cssClass: "sepia",
          label: "Sepia theme",
        };
      case "night":
        return {
          cssClass: "dark",
          label: "Dark theme",
        };
      default:
        return {
          cssClass: "light",
          label: "Light theme",
        };
    }
  };

  const updateFont = (fontValue: string): void => {
    setFont(fontValue);
    onUpdateTheme({ fontSelector: fontValue });
  };

  const onAppBackgroundChange = (checked: boolean): void => {
    setIsAppInBackground(checked);
    viewerApi.setIsAppBackgroundStatus(checked);
  };

  const handlePrint = (): void => {
    viewerApi.print?.();
  };

  const handleWidgetTypeChange = (nextType: EtextWidgetType): void => {
    setWidgetType(nextType);
    setWidgetPayloadJson(formatPayloadJson(getDefaultPayloadForType(nextType)));
  };

  const handleLoadTestWidget = (): void => {
    if (!onLoadTestWidget) {
      return;
    }

    const payload = parseEtextWidgetPayload(widgetPayloadJson);
    if (!payload) {
      return;
    }

    onLoadTestWidget({ widgetType, payload });
  };

  return (
    <div className="drawer settings-drawer">
      <div className="header">
        <h3 className="title">Settings</h3>
        <button className="close-button" aria-label="Close settings" onClick={onClose}>
          <div className="close-icon" />
        </button>
      </div>

      {!minimalShell && viewerApi.print && (
        <div className="theme-button-row print-button-row">
          <button
            type="button"
            className="action-button print-button"
            aria-label="Print"
            onClick={handlePrint}
          >
            Print
          </button>
        </div>
      )}
      {!minimalShell && (
        <>
          <div className="theme-button-row-label">App background</div>
          <div className="app-background-switch">
            <label className="switch">
              Is App in Background
              <input
                name="app-background-switch"
                type="checkbox"
                checked={isAppInBackground}
                onChange={(e): void => onAppBackgroundChange(e.target.checked)}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </>
      )}
      <div className="theme-button-row-label">Select Theme</div>
      <div className="theme-button-row">
        {themeVariants?.map((themeVariant: IThemeVariant) => (
          <button
            key={themeVariant.variantId}
            className={`action-button ${themeVariant.cssClass}`}
            aria-label={themeVariant.label}
            onClick={(): void => {
              onUpdateTheme({
                variantId: themeVariant.variantId,
              });
            }}
          >
            {themeVariant.variantName}
          </button>
        ))}
      </div>

      <div className="theme-button-row-label">Select Text Size</div>
      <div className="theme-button-row">
        <div className="range-slider">
          <input
            type="range"
            min="12"
            max="21"
            aria-label="font-size-slider"
            value={fontSize}
            onChange={(event): void => setFontSize(Number.parseInt(event.target.value))}
            onPointerUp={(): void => onUpdateTheme({ fontSize })}
          />
        </div>
      </div>

      <div className="theme-button-row-label">Select Font</div>
      <div>
        {FONT_OPTIONS.map((item) => (
          <label className="theme-font-option" key={`font_${item.value}`}>
            <input
              type="radio"
              name="font"
              value={item.value}
              checked={font === item.value}
              onChange={(e): void => updateFont(e.target.value)}
            />{" "}
            {item.label}
          </label>
        ))}
      </div>

      <div className="theme-button-row-label">Upper/Lowercase</div>
      <div className="theme-button-row">
        <button
          className={`action-button ${currentTheme.textTransform === "default" ? "active" : ""}`}
          aria-label="Default case"
          onClick={(): void => {
            onUpdateTheme({ textTransform: "default" });
          }}
        >
          Abcd
        </button>
        <button
          className={`action-button ${currentTheme.textTransform === "caps" ? "active" : ""}`}
          aria-label="All uppercase"
          onClick={(): void => {
            onUpdateTheme({ textTransform: "caps" });
          }}
        >
          ABCD
        </button>
      </div>

      <div className="theme-button-row-label">Select Line Spacing</div>
      <div className="theme-button-row">
        <button
          className={`action-button ${currentTheme.lineSpacing === "default" ? "active" : ""}`}
          aria-label="Default line height"
          onClick={(): void => {
            onUpdateTheme({ lineSpacing: "default" });
          }}
        >
          Default Line Spacing
        </button>
        <button
          className={`action-button ${currentTheme.lineSpacing === "wide" ? "active" : ""}`}
          aria-label="Wide line height"
          onClick={(): void => {
            onUpdateTheme({ lineSpacing: "wide" });
          }}
        >
          Wider Line Spacing
        </button>
        <button
          className={`action-button ${currentTheme.lineSpacing === "wider" ? "active" : ""}`}
          aria-label="Wider line height"
          onClick={(): void => {
            onUpdateTheme({ lineSpacing: "wider" });
          }}
        >
          Widest Line Spacing
        </button>
      </div>
      {showTestWidgetButton && onLoadTestWidget && (
        <>
          <div className="theme-button-row-label">QE tools</div>
          <div className="theme-button-row-label">Widget type</div>
          <div className="theme-button-row">
            <label className="theme-font-option">
              <input
                type="radio"
                name="etext-widget-type"
                value="mcq"
                checked={widgetType === "mcq"}
                onChange={(): void => handleWidgetTypeChange("mcq")}
              />{" "}
              MCQ
            </label>
            <label className="theme-font-option">
              <input
                type="radio"
                name="etext-widget-type"
                value="feedback"
                checked={widgetType === "feedback"}
                onChange={(): void => handleWidgetTypeChange("feedback")}
              />{" "}
              Feedback
            </label>
          </div>
          <div className="theme-button-row-label">Widget payload (JSON)</div>
          {widgetType === "feedback" && (
            <p className="theme-button-row-label" style={{ fontWeight: 400, marginTop: 0 }}>
              Feedback widget uses <code>assignmentId</code>. Optional <code>text</code> pre-fills the
              chat input (dev-shell QE only; widget.js ignores <code>feedbackResponse</code>).
            </p>
          )}
          <div className="theme-button-row">
            <textarea
              aria-label="eText widget payload JSON"
              value={widgetPayloadJson}
              style={{ whiteSpace: "pre-wrap", width: "100%" }}
              onChange={(e): void => setWidgetPayloadJson(e.target.value)}
              rows={12}
            />
          </div>
          <div className="theme-button-row">
            <button
              type="button"
              className="action-button restart-button"
              aria-label="Load test eText widget"
              onClick={handleLoadTestWidget}
            >
              Load test eText widget
            </button>
          </div>
        </>
      )}
      {!minimalShell && (
        <>
          <div className="theme-button-row-label">Visibility</div>
          <div className="show-highlights-option">
            <label>
              <input
                type="checkbox"
                checked={showHighlights}
                onChange={handleVisibility}
                data-testid="show-highlights"
              />{" "}
              Show highlights
            </label>
          </div>

          <div className="theme-button-row-label">Accessibility</div>
          <div className="theme-button-row">
            <button
              type="button"
              className="action-button restart-button"
              aria-label="Restart"
              onClick={(): void => viewerApi.restart(parentActivityId)}
            >
              Restart parent activity
            </button>
          </div>

          <div className="theme-button-row-label">Continuous Scroll</div>
          <div className="theme-button-row">
            <button
              className={`action-button ${currentTheme.continuousScroll ? "active" : ""}`}
              aria-label="Scroll"
              onClick={(): void => {
                onUpdateTheme({ continuousScroll: true });
              }}
            >
              Scroll
            </button>
            <button
              className={`action-button ${!currentTheme.continuousScroll ? "active" : ""}`}
              aria-label="Swipe"
              onClick={(): void => {
                onUpdateTheme({ continuousScroll: false });
              }}
            >
              Swipe
            </button>
          </div>
          <div className="theme-button-row-label">Highlight Options</div>
          <div className="theme-button-row">
            <textarea
              value={highlightOptions}
              style={{ whiteSpace: "pre-wrap" }}
              onChange={(e): void => onUpdateHighlightOptions(e.target.value)}
              rows={30}
              cols={50}
            ></textarea>
          </div>
        </>
      )}
    </div>
  );
};
